import { useEffect, useMemo, useRef, useState } from 'react';
import { Text, TextInput, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import * as Crypto from 'expo-crypto';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import Chip from '@/components/General/Chip';
import SelectInput from '@/components/General/inputs/SelectInput';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import Callout from '@/components/General/Callout';
import { Mutation } from '@/utils/react-query/Mutations';
import {
    FOLLOW_UP_TOUCH_TYPES,
    FOLLOW_UP_CONNECTION_RESULTS,
    FOLLOW_UP_DISPOSITIONS,
    NOTE_VISIBILITY_OPTIONS,
    followUpResultIsConnected,
} from '@/utils/constants/followUp';

/**
 * LogTouchSheet — the core Follow Up interaction (spec §6.5/§9).
 *
 * Opening the sheet creates NOTHING — only the explicit Log Touch submit
 * calls follow_up_log_touch. A client idempotencyKey is generated once per
 * sheet open (not per submit) so a retried submit replays to the same touch
 * server-side instead of duplicating it.
 *
 * With `touch` set the sheet becomes a correction form (prefilled; submits
 * follow_up_correct_touch with only changed fields; no note section —
 * corrections never rewrite linked notes).
 *
 * `careEpisodeID` (§25.9, optional) links the touch to a care episode
 * so it lands on the episode's shared activity timeline — the server
 * validates that the episode belongs to this person.
 *
 * `stepContext` (§25.7, optional) marks logging from an ACTIVE Path step:
 * { taskInstanceID, expectedChannel, enrollmentID? }. The expected channel
 * prefills Type (never the outcome/receptiveness); the server links the
 * touch to that step and finishes its Task atomically. A connected result
 * then requires the actual receptiveness. Standalone logging keeps the
 * same contract — no stricter global requirement.
 */
const LogTouchSheet = ({ isOpen, onClose, organizationID, personID, personName, touch = null, careEpisodeID = null, stepContext = null }) => {
    const isCorrection = !!touch;
    const idempotencyKeyRef = useRef(null);
    const expectedChannel = stepContext?.expectedChannel || null;

    const [type, setType] = useState(touch?.type || expectedChannel || 'PHONE_CALL');
    const [connectionResult, setConnectionResult] = useState(touch?.connectionResult || null);
    const [disposition, setDisposition] = useState(touch?.responseDisposition || null);
    const [occurredAt, setOccurredAt] = useState(touch?.occurredAt ? new Date(touch.occurredAt) : new Date());
    const [noteContent, setNoteContent] = useState('');
    const [noteVisibility, setNoteVisibility] = useState('FOLLOW_UP');

    // Fresh idempotency key + form reset each time the sheet opens. The key
    // survives retries inside one open session — a re-submit after a network
    // failure replays server-side instead of duplicating the touch.
    useEffect(() => {
        if (isOpen) {
            idempotencyKeyRef.current = Crypto.randomUUID();
            setType(touch?.type || expectedChannel || 'PHONE_CALL');
            setConnectionResult(touch?.connectionResult || null);
            setDisposition(touch?.responseDisposition || null);
            setOccurredAt(touch?.occurredAt ? new Date(touch.occurredAt) : new Date());
            setNoteContent('');
            setNoteVisibility('FOLLOW_UP');
        } else {
            idempotencyKeyRef.current = null;
        }
    }, [isOpen]);

    const resultOptions = useMemo(
        () => FOLLOW_UP_CONNECTION_RESULTS[type] || [],
        [type]
    );

    // Type switch clears a result that no longer applies to the channel.
    const handleTypeChange = (nextType) => {
        setType(nextType);
        if (connectionResult && !FOLLOW_UP_CONNECTION_RESULTS[nextType]?.some((r) => r.value === connectionResult)) {
            setConnectionResult(null);
        }
    };

    // Honest preview of how the server will classify this result.
    const connectedPreview = useMemo(
        () => (connectionResult ? followUpResultIsConnected(type, connectionResult) : null),
        [type, connectionResult]
    );

    const showNoContactWarning = disposition === 'REQUESTED_NO_CONTACT';
    // §25.7 — a connected result logged from an active Path step must
    // record the actual receptiveness (server enforces; never invented).
    const needsDisposition = !!stepContext?.taskInstanceID
        && !isCorrection
        && !!connectionResult
        && followUpResultIsConnected(type, connectionResult)
        && !disposition;

    const logMutation = Mutation.followUp.logTouch({
        onSuccess: (data) => {
            Toast.show({
                type: 'success',
                text2: data?.isConnected ? 'Touch logged — connected' : 'Touch logged — attempt recorded',
            });
            onClose?.();
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to log the touch. Please try again.' });
            }
        },
    });

    const correctMutation = Mutation.followUp.correctTouch({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Touch updated' });
            onClose?.();
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to update the touch. Please try again.' });
            }
        },
    });

    const submitting = logMutation.isPending || correctMutation.isPending;

    const handleSubmit = () => {
        if (!type || !connectionResult || submitting || needsDisposition) return;
        const occurred = occurredAt ? new Date(occurredAt) : new Date();
        // Client guard mirroring the server rule — future "touches" can't
        // masquerade as completed care (spec §9.3).
        if (occurred.getTime() > Date.now() + 5 * 60 * 1000) {
            Toast.show({ type: 'error', text2: "A touch can't be dated in the future — future plans belong in Tasks." });
            return;
        }

        if (isCorrection) {
            const patch = {};
            if (type !== touch.type) patch.type = type;
            if (connectionResult !== touch.connectionResult) patch.connectionResult = connectionResult;
            if ((disposition || null) !== (touch.responseDisposition || null)) patch.responseDisposition = disposition || '';
            const prev = touch.occurredAt ? new Date(touch.occurredAt).getTime() : null;
            if (prev !== occurred.getTime()) patch.occurredAt = occurred.toISOString();
            correctMutation.mutate({ organizationID, personID, touchID: touch.id, patch });
            return;
        }

        const payload = {
            personID,
            type,
            connectionResult,
            occurredAt: occurred.toISOString(),
            idempotencyKey: idempotencyKeyRef.current,
        };
        if (careEpisodeID) payload.careEpisodeID = careEpisodeID;
        if (stepContext?.taskInstanceID) payload.taskInstanceID = stepContext.taskInstanceID;
        if (disposition) payload.responseDisposition = disposition;
        if (noteContent.trim()) {
            payload.note = { content: noteContent.trim(), visibility: noteVisibility };
        }
        logMutation.mutate({ organizationID, personID, payload });
    };

    // Confirm always renders — a missing Result reports instead of hiding the
    // button (same contract as the automation trigger sheet).
    const handleConfirm = () => {
        if (!connectionResult) {
            Toast.show({ type: 'error', text2: 'Choose a result for the touch.' });
            return;
        }
        handleSubmit();
    };

    return (
        <SheetModal
            isOpen={isOpen}
            title={isCorrection ? 'Correct Touch' : 'Log Touch'}
            subtitle={personName || undefined}
            cancelText="Cancel"
            confirmText={isCorrection ? 'Save Correction' : 'Log Touch'}
            onCancel={onClose}
            onClose={onClose}
            onConfirm={handleConfirm}
            loading={submitting}
        >
            <View style={styles.body}>
                <Text style={styles.label}>Type</Text>
                <View style={styles.chipRow}>
                    {FOLLOW_UP_TOUCH_TYPES.map((t) => (
                        <Chip
                            key={t.value}
                            testID={`log-touch-type-${t.value}`}
                            colorVariant={type === t.value ? 'primary' : 'textSecondary'}
                            selected={type === t.value}
                            icon={t.icon}
                            iconSize={14}
                            onPress={() => handleTypeChange(t.value)}
                            style={styles.optionChip}
                            textStyle={styles.optionChipText}
                        >
                            {t.label}
                        </Chip>
                    ))}
                </View>

                <Text style={styles.label}>Result <Text style={styles.required}>*</Text></Text>
                <View style={styles.chipRow}>
                    {resultOptions.map((r) => (
                        <Chip
                            key={r.value}
                            testID={`log-touch-result-${r.value}`}
                            colorVariant={connectionResult === r.value ? 'primary' : 'textSecondary'}
                            selected={connectionResult === r.value}
                            onPress={() => setConnectionResult(r.value)}
                            style={styles.optionChip}
                            textStyle={styles.optionChipText}
                        >
                            {r.label}
                        </Chip>
                    ))}
                </View>
                {connectedPreview === true && (
                    <Text testID="log-touch-connected-preview" style={styles.connectedHint}>This counts as a connected conversation.</Text>
                )}
                {connectedPreview === false && (
                    <Text testID="log-touch-attempt-preview" style={styles.attemptHint}>This counts as an attempt — it won&rsquo;t update the last-connected date.</Text>
                )}

                <SelectInput
                    title={needsDisposition || (stepContext?.taskInstanceID && connectedPreview === true) ? 'Response' : 'Response (optional)'}
                    required={!!stepContext?.taskInstanceID && connectedPreview === true}
                    placeholder="How did they respond?"
                    value={disposition}
                    options={FOLLOW_UP_DISPOSITIONS}
                    onUpdate={setDisposition}
                />
                {needsDisposition && (
                    <Text style={styles.requiredHint} testID="log-touch-response-required">
                        Record how they responded — a connected result needs an actual response.
                    </Text>
                )}

                {showNoContactWarning && (
                    <Callout
                        testID="log-touch-no-contact-warning"
                        type="warning"
                        title="Requested No Contact"
                        message={stepContext?.taskInstanceID
                            ? 'This stops the Path for this person immediately and records a contact warning. Team members will see the stop reason until it is resolved.'
                            : "This records a contact warning on the person's Follow Up profile. Team members will see the warning until a manager resolves it."}
                    />
                )}

                <DatePickerInput
                    title="When"
                    mode="datetime"
                    value={occurredAt}
                    onUpdate={(d) => setOccurredAt(d || new Date())}
                    maximumDate={new Date()}
                />

                {!isCorrection && (
                    <>
                        <Text style={styles.label}>Note (optional)</Text>
                        <TextInput
                            testID="log-touch-note-input"
                            style={styles.noteInput}
                            multiline
                            numberOfLines={3}
                            placeholder="Add a note about this touch..."
                            placeholderTextColor="#999"
                            value={noteContent}
                            onChangeText={setNoteContent}
                        />
                        {!!noteContent.trim() && (
                            <SelectInput
                                title="Note visibility"
                                value={noteVisibility}
                                options={NOTE_VISIBILITY_OPTIONS}
                                hideClearButton
                                onUpdate={(v) => setNoteVisibility(v || 'FOLLOW_UP')}
                            />
                        )}
                    </>
                )}
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    body: { paddingBottom: 16 },
    label: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        marginTop: 12,
        marginBottom: 6,
    },
    required: { color: colors.error },
    chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    // Shared Chip sized up to a tap target — selected state comes from
    // colorVariant (primary vs textSecondary).
    optionChip: {
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        gap: 6,
    },
    optionChipText: {
        fontSize: 13,
    },
    connectedHint: {
        fontSize: 12,
        color: colors.success,
        marginTop: 6,
        fontFamily: fonts.medium,
    },
    requiredHint: {
        fontSize: 12,
        color: colors.error,
        marginTop: 6,
    },
    attemptHint: {
        fontSize: 12,
        color: colors.textSecondary,
        marginTop: 6,
    },
    noteInput: {
        fontSize: 14,
        color: colors.text,
        backgroundColor: colors.inputBackground,
        borderRadius: 10,
        padding: 12,
        textAlignVertical: 'top',
        minHeight: 72,
        _web: { outlineStyle: 'none' },
    },
}));

export default LogTouchSheet;
